// VISUAL-ONLY muscle-head breakdown for the 3D detail viewer. Deliberately kept
// out of the global `MuscleId` enum: these sub-heads aren't trained/tracked as
// separate muscles (no volume targets, no exercise mapping). They only let the
// anatomy model highlight individual heads of a muscle group.
// ponytail: local to the model. Promote to MuscleId only if an exercise ever
// needs to target one head (that's a schema migration, not this).
//
// Patterns match the Z-Anatomy GLB node names AFTER three's GLTFLoader sanitizes
// them: spaces→"_", dots stripped, and a "_1"/"_2" de-dupe suffix appended. So
// "Long head of biceps brachii.r" arrives as "Long_head_of_biceps_brachiir_1".
// Match word gaps with [_\s]; anchor on the distinctive muscle words.

export type DetailPart = Readonly<{
  id: string;
  label: string;
  color: string;
  pattern: RegExp;
}>;

export type MuscleDetailGroup = Readonly<{
  id: string;
  label: string;
  parts: ReadonlyArray<DetailPart>;
}>;

const GREEN = "#4ade80";
const CYAN = "#22c3ff";
const RED = "#ef4444";
const AMBER = "#f59e0b";

export const MUSCLE_DETAIL_GROUPS: ReadonlyArray<MuscleDetailGroup> = [
  {
    id: "biceps",
    label: "Biceps",
    parts: [
      {
        id: "long_head",
        label: "Long Head",
        color: GREEN,
        pattern: /long[_\s]head[_\s]of[_\s]biceps[_\s]brachii/i,
      },
      {
        id: "short_head",
        label: "Short Head",
        color: CYAN,
        pattern: /short[_\s]head[_\s]of[_\s]biceps[_\s]brachii/i,
      },
      { id: "brachialis", label: "Brachialis", color: RED, pattern: /\bbrachialis[_\s]muscle/i },
    ],
  },
  {
    id: "triceps",
    label: "Triceps",
    parts: [
      {
        id: "long",
        label: "Long Head",
        color: GREEN,
        pattern: /long[_\s]head[_\s]of[_\s]triceps[_\s]brachii/i,
      },
      {
        id: "lateral",
        label: "Lateral Head",
        color: CYAN,
        pattern: /lateral[_\s]head[_\s]of[_\s]triceps[_\s]brachii/i,
      },
      {
        id: "medial",
        label: "Medial Head",
        color: AMBER,
        pattern: /medial[_\s]head[_\s]of[_\s]triceps[_\s]brachii/i,
      },
    ],
  },
  {
    id: "deltoid",
    label: "Deltoid",
    parts: [
      {
        id: "front",
        label: "Front (Clavicular)",
        color: GREEN,
        pattern: /clavicular[_\s]part[_\s]of[_\s]deltoid/i,
      },
      {
        id: "side",
        label: "Side (Acromial)",
        color: CYAN,
        pattern: /acromial[_\s]part[_\s]of[_\s]deltoid/i,
      },
      {
        id: "rear",
        label: "Rear (Spinal)",
        color: RED,
        pattern: /scapular[_\s]spinal[_\s]part[_\s]of[_\s]deltoid/i,
      },
    ],
  },
  {
    id: "pecs",
    label: "Pecs",
    // Pec major has TWO anatomical heads: clavicular (upper) and sternocostal.
    // The "abdominal" region is the lowest sternocostal fibers, not a 3rd head —
    // kept as a separate highlightable region but labeled as such.
    parts: [
      {
        id: "clav",
        label: "Clavicular Head (upper)",
        color: GREEN,
        pattern: /clavicular[_\s]head[_\s]of[_\s]pectoralis/i,
      },
      {
        id: "stern",
        label: "Sternocostal Head (mid)",
        color: CYAN,
        pattern: /sternocostal[_\s]head[_\s]of[_\s]pectoralis/i,
      },
      {
        id: "abd",
        label: "Sternocostal Head (lower fibers)",
        color: RED,
        pattern: /abdominal[_\s]part[_\s]of[_\s]pectoralis/i,
      },
    ],
  },
  {
    id: "back",
    label: "Back",
    // The lat isn't split into anatomical heads — this group instead separates
    // the distinct back-builder muscles (lat vs teres major vs rhomboids) so a
    // row can accent the mid-back and a pulldown the lats. Teres major matches
    // "major" only (teres minor is a rotator-cuff muscle, not a lat synergist).
    parts: [
      { id: "lats", label: "Latissimus Dorsi", color: GREEN, pattern: /latissimus[_\s]dorsi/i },
      { id: "teres", label: "Teres Major", color: CYAN, pattern: /teres[_\s]major/i },
      { id: "rhomboids", label: "Rhomboids", color: RED, pattern: /rhomboid[_\s](major|minor)/i },
    ],
  },
  {
    id: "traps",
    label: "Traps",
    parts: [
      {
        id: "upper",
        label: "Upper (Descending)",
        color: GREEN,
        pattern: /descending[_\s]part[_\s]of[_\s]trapezius/i,
      },
      {
        id: "mid",
        label: "Middle (Transverse)",
        color: CYAN,
        pattern: /transverse[_\s]part[_\s]of[_\s]trapezius/i,
      },
      {
        id: "lower",
        label: "Lower (Ascending)",
        color: RED,
        pattern: /ascending[_\s]part[_\s]of[_\s]trapezius/i,
      },
    ],
  },
  {
    id: "quads",
    label: "Quads",
    parts: [
      { id: "rf", label: "Rectus Femoris", color: RED, pattern: /rectus[_\s]femoris/i },
      { id: "vl", label: "Vastus Lateralis", color: CYAN, pattern: /vastus[_\s]lateralis/i },
      { id: "vm", label: "Vastus Medialis", color: GREEN, pattern: /vastus[_\s]medialis/i },
      { id: "vi", label: "Vastus Intermedius", color: AMBER, pattern: /vastus[_\s]intermedius/i },
    ],
  },
  {
    id: "hamstrings",
    label: "Hamstrings",
    parts: [
      {
        id: "bf_long",
        label: "Biceps Femoris (Long)",
        color: GREEN,
        pattern: /long[_\s]head[_\s]of[_\s]biceps[_\s]femoris/i,
      },
      {
        id: "bf_short",
        label: "Biceps Femoris (Short)",
        color: CYAN,
        pattern: /short[_\s]head[_\s]of[_\s]biceps[_\s]femoris/i,
      },
      { id: "st", label: "Semitendinosus", color: RED, pattern: /semitendinosus[_\s]muscle/i },
      { id: "sm", label: "Semimembranosus", color: AMBER, pattern: /semimembranosus[_\s]muscle/i },
    ],
  },
  {
    id: "calves",
    label: "Calves",
    parts: [
      {
        id: "gm",
        label: "Gastroc (Medial)",
        color: GREEN,
        pattern: /medial[_\s]head[_\s]of[_\s]gastrocnemius/i,
      },
      {
        id: "gl",
        label: "Gastroc (Lateral)",
        color: CYAN,
        pattern: /lateral[_\s]head[_\s]of[_\s]gastrocnemius/i,
      },
      { id: "sol", label: "Soleus", color: RED, pattern: /\bsoleus[_\s]muscle/i },
    ],
  },
  {
    id: "glutes",
    label: "Glutes",
    parts: [
      { id: "max", label: "Maximus", color: GREEN, pattern: /gluteus[_\s]maximus[_\s]muscle/i },
      { id: "med", label: "Medius", color: CYAN, pattern: /gluteus[_\s]medius[_\s]muscle/i },
      { id: "min", label: "Minimus", color: RED, pattern: /gluteus[_\s]minimus[_\s]muscle/i },
    ],
  },
];

// qualified id `${groupId}:${partId}` -> accent color, for whole-body highlight.
export const DETAIL_PART_COLOR: ReadonlyMap<string, string> = new Map(
  MUSCLE_DETAIL_GROUPS.flatMap((g) => g.parts.map((p) => [`${g.id}:${p.id}`, p.color] as const)),
);

// Bursae/fascia/tendons wrap the muscles and share their names (e.g.
// "Trochanteric_bursa_of_gluteus_maximus_muscle"); drop them so a head's
// pattern doesn't grab the sheath around it.
const EXCLUDE = /bursa|fascia|tendon|aponeurosis|retinaculum/i;

// `\bbrachialis` must NOT match "Coracobrachialis" (one word, no boundary), so
// the biceps heads and brachialis stay distinct.
export const classifyDetailPart = (group: MuscleDetailGroup, meshChain: string): string | null => {
  if (EXCLUDE.test(meshChain)) return null;

  return group.parts.find((part) => part.pattern.test(meshChain))?.id ?? null;
};

// Match a mesh against every group; returns the qualified id `${group}:${part}`.
export const classifyAnyDetailPart = (meshChain: string): string | null => {
  if (EXCLUDE.test(meshChain)) return null;

  for (const group of MUSCLE_DETAIL_GROUPS) {
    const partId = group.parts.find((part) => part.pattern.test(meshChain))?.id;
    if (partId !== undefined) return `${group.id}:${partId}`;
  }

  return null;
};
